import React from 'react';
import { Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPaste, faPlus, faTrash, faNoteSticky } from '@fortawesome/free-solid-svg-icons';
import ProductRow from '../ProductRow/ProductRow';
import type { MenuItem, Table, TableProduct } from '../../types';
import { useI18n } from '../../i18n/I18nContext';
import NumberInput from '../NumberInput/NumberInput';
import { normalizeSearchText } from '../../utils/textSearch';
import { formatEuro } from '../../utils/currency';

export interface TableProductsPanelProps {
  table: Table;
  newProductInputRef?: React.Ref<HTMLInputElement>;
  readOnly?: boolean;
  showPrices?: boolean;
  canPaste: boolean;
  menuItems: MenuItem[];
  newProductName: string;
  newProductNote: string;
  newProductQty: number;
  newProductHandling: boolean;
  newProductDelivered: boolean;
  newProductPaid: boolean;
  dragOverIndex: number | null;
  onNewProductNameChange: (value: string) => void;
  onNewProductNoteChange: (value: string) => void;
  onNewProductQtyChange: (value: number) => void;
  onNewProductHandlingChange: (value: boolean) => void;
  onNewProductDeliveredChange: (value: boolean) => void;
  onNewProductPaidChange: (value: boolean) => void;
  onAddProduct: () => void;
  onCopyProducts: () => void;
  onPasteProducts: () => void;
  onRemoveAllProducts: () => void;
  onToggleHandling: (index: number) => void;
  onToggleDelivered: (index: number) => void;
  onTogglePaid: (index: number) => void;
  onEditQty: (index: number, value: number) => void;
  onEditName: (index: number, value: string) => void;
  onEditNote: (index: number, value: string) => void;
  onRemoveProduct: (index: number) => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number, e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (index: number) => void;
}

const TableProductsPanel: React.FC<TableProductsPanelProps> = ({
  table,
  newProductInputRef,
  readOnly = false,
  showPrices = true,
  canPaste,
  menuItems,
  newProductName,
  newProductNote,
  newProductQty,
  newProductHandling,
  newProductDelivered,
  newProductPaid,
  dragOverIndex,
  onNewProductNameChange,
  onNewProductNoteChange,
  onNewProductQtyChange,
  onNewProductHandlingChange,
  onNewProductDeliveredChange,
  onNewProductPaidChange,
  onAddProduct,
  onCopyProducts,
  onPasteProducts,
  onRemoveAllProducts,
  onToggleHandling,
  onToggleDelivered,
  onTogglePaid,
  onEditQty,
  onEditName,
  onEditNote,
  onRemoveProduct,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const { t } = useI18n();
  const listId = 'table-modal-product-options';
  const [expandedNoteKey, setExpandedNoteKey] = React.useState<string | null>(null);
  const autocompleteOptions = React.useMemo(() => {
    const options: Array<{ key: string; value: string; label?: string }> = [];
    const seenValues = new Set<string>();

    menuItems.forEach((item) => {
      const originalValue = String(item.name ?? '').trim();
      if (originalValue && !seenValues.has(originalValue)) {
        seenValues.add(originalValue);
        options.push({
          key: `${item.id}:original`,
          value: originalValue,
        });
      }

      const normalizedAlias = normalizeSearchText(originalValue);
      if (normalizedAlias && normalizedAlias !== originalValue.toLowerCase() && !seenValues.has(normalizedAlias)) {
        seenValues.add(normalizedAlias);
        options.push({
          key: `${item.id}:normalized`,
          value: normalizedAlias,
          label: originalValue,
        });
      }
    });

    return options;
  }, [menuItems]);
  const hasExistingNotes = Boolean(table.products?.some((item) => item.note?.trim()));
  const noteColumnExpanded = expandedNoteKey !== null || hasExistingNotes || Boolean(newProductNote.trim());
  const newProductNoteExpanded = expandedNoteKey === 'new' || Boolean(newProductNote.trim());
  const matchedNewProduct = React.useMemo(
    () => menuItems.find((item) => normalizeSearchText(item.name) === normalizeSearchText(newProductName.trim())),
    [menuItems, newProductName],
  );

  const handleNewProductKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onAddProduct();
    }
  };

  React.useEffect(() => {
    if (expandedNoteKey === 'new' && !newProductNote.trim()) {
      return;
    }

    if (expandedNoteKey && expandedNoteKey !== 'new') {
      const noteOwnerStillExists = table.products?.some((item, index) => `${item.id ?? index}` === expandedNoteKey);
      if (!noteOwnerStillExists) {
        setExpandedNoteKey(null);
      }
    }
  }, [expandedNoteKey, newProductNote, table.products]);

  return (
    <>
      <h4
        style={{
          marginTop: 12,
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{t('table.products')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          {canPaste && (
            <button type="button" title={t('table.pasteProducts')} className="paste-icon" onClick={onPasteProducts} disabled={readOnly}>
              <FontAwesomeIcon icon={faPaste} />
            </button>
          )}
          <button type="button" title={t('table.copyProducts')} className="copy-icon" onClick={onCopyProducts} disabled={readOnly}>
            <FontAwesomeIcon icon={faCopy} />
          </button>
          <div style={{ width: 10 }} />
          <button type="button" className="delete-icon" title={t('table.removeAllProducts')} onClick={onRemoveAllProducts} disabled={readOnly}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </h4>
      <div className={`product-list-header${noteColumnExpanded ? ' product-list-header--note-expanded' : ''}`} role="presentation">
        <span className="product-list-header__spacer" aria-hidden="true" />
        <span className="product-list-header__label">{t('table.handling')}</span>
        <span className="product-list-header__label">{t('table.delivered')}</span>
        <span className="product-list-header__label">{t('table.paid')}</span>
        <span className="product-list-header__label">{t('table.quantityShort')}</span>
        <span className="product-list-header__label product-list-header__label--name">{t('table.product')}</span>
        <span className="product-list-header__label product-list-header__label--note">{t('table.productNote')}</span>
        <span className="product-list-header__spacer" aria-hidden="true" />
      </div>
      <div className="product-list">
        {table.products?.map((item, index) => (
          <ProductRow
            key={item.id || index}
            item={item}
            listId={listId}
            readOnly={readOnly}
            showPrices={showPrices}
            noteExpanded={expandedNoteKey === `${item.id ?? index}` || Boolean(item.note?.trim())}
            onToggleHandling={() => onToggleHandling(index)}
            onToggleDelivered={() => onToggleDelivered(index)}
            onTogglePaid={() => onTogglePaid(index)}
            onEditQty={value => onEditQty(index, value)}
            onEditName={value => onEditName(index, value)}
            onEditNote={value => onEditNote(index, value)}
            onOpenNote={() => setExpandedNoteKey(`${item.id ?? index}`)}
            onCloseNote={() => setExpandedNoteKey(null)}
            onRemove={() => onRemoveProduct(index)}
            draggable={!readOnly}
            onDragStart={readOnly ? undefined : () => onDragStart(index)}
            onDragOver={readOnly ? undefined : e => onDragOver(index, e)}
            onDrop={readOnly ? undefined : () => onDrop(index)}
            isDragOver={dragOverIndex === index}
          />
        ))}
          <div className={`product-row new-product-row${newProductNoteExpanded ? ' product-row--note-expanded' : ''}`}>
          <span className="drag-handle drag-handle-placeholder" aria-hidden="true" />
          <input
            type="checkbox"
            checked={newProductHandling}
            disabled={readOnly}
            onChange={(event) => onNewProductHandlingChange(event.target.checked)}
            title={t('table.handling')}
          />
          <input
            type="checkbox"
            checked={newProductDelivered}
            disabled={readOnly}
            onChange={(event) => onNewProductDeliveredChange(event.target.checked)}
            title={t('table.delivered')}
          />
          <div className="paid-toggle-container">
            <Switch
              checked={newProductPaid}
              disabled={readOnly}
              onChange={(event) => onNewProductPaidChange(event.target.checked)}
              size="small"
            />
            {newProductPaid && <span className="paid-label">{t('table.paid')}</span>}
          </div>
          <NumberInput
            value={newProductQty}
            onChange={onNewProductQtyChange}
            min={1}
            className="number-input--quantity"
            onKeyDown={handleNewProductKeyDown}
            disabled={readOnly}
          />
          <div className="product-name-cell">
            <input
              ref={newProductInputRef}
              type="text"
              list={listId}
              value={newProductName}
              placeholder={t('table.productName')}
              disabled={readOnly}
              translate="no"
              onChange={e => onNewProductNameChange(e.target.value)}
              onKeyDown={handleNewProductKeyDown}
            />
            {showPrices && typeof matchedNewProduct?.price === 'number' ? (
              <span className="product-price-badge">{formatEuro(matchedNewProduct.price)}</span>
            ) : null}
          </div>
          {newProductNoteExpanded ? (
            <input
              type="text"
              value={newProductNote}
              placeholder={t('table.productNote')}
              disabled={readOnly}
              translate="no"
              autoFocus={expandedNoteKey === 'new'}
              onChange={e => onNewProductNoteChange(e.target.value)}
              onKeyDown={handleNewProductKeyDown}
              onBlur={() => {
                if (!newProductNote.trim()) {
                  setExpandedNoteKey(null);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className={`product-note-toggle${newProductNote.trim() ? ' product-note-toggle--filled' : ''}`}
              onClick={() => setExpandedNoteKey('new')}
              aria-label={t('table.addProductNote')}
              title={newProductNote.trim() ? `${t('table.productNote')}: ${newProductNote}` : t('table.addProductNote')}
              disabled={readOnly}
            >
              <FontAwesomeIcon icon={faNoteSticky} />
            </button>
          )}
          <button type="button" className="add-icon" onClick={onAddProduct} aria-label={t('table.addProduct')} disabled={readOnly}>
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>
      <datalist id={listId}>
        {autocompleteOptions.map((option) => (
          <option key={option.key} value={option.value} label={option.label}>
            {option.label ?? option.value}
          </option>
        ))}
      </datalist>
    </>
  );
};

export default TableProductsPanel;
